import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Bed, Maximize, Building, Download, Phone, Loader2, ChevronLeft, ChevronRight, Play, IndianRupee, Layers, SquareIcon, Trees, Send } from 'lucide-react';
import { findProperty, imageUrl, FALLBACK_IMAGE } from '@/lib/sheetData';
import { LeadForm } from '@/components/LeadForm';
import { REQUEST_TYPES } from '@/lib/web3forms';
import { getConfig } from '@/lib/runtimeConfig';

const ImageCarousel = ({ images, videoUrl }) => {
  const [current, setCurrent] = useState(0);
  const normalizedImages = (images || []).map(imageUrl).filter(Boolean);
  const allMedia = normalizedImages.length > 0 ? normalizedImages : [FALLBACK_IMAGE];
  const isVideo = (url) => url && (url.includes('.mp4') || url.includes('.webm') || url.includes('.mov'));

  return (
    <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
      <div className="absolute inset-0 bg-black overflow-hidden group">
      <AnimatePresence mode="wait">
        {isVideo(allMedia[current]) ? (
          <motion.video key={current} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} src={allMedia[current]} className="w-full h-full object-cover" autoPlay muted loop playsInline controls />
        ) : (
          <motion.img key={current} initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} src={allMedia[current]} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }} />
        )}
      </AnimatePresence>

      {allMedia.length > 1 && (
        <>
          <button onClick={() => setCurrent(p => (p - 1 + allMedia.length) % allMedia.length)} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white/40"><ChevronLeft size={22} /></button>
          <button onClick={() => setCurrent(p => (p + 1) % allMedia.length)} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white/40"><ChevronRight size={22} /></button>
        </>
      )}

      {videoUrl && (
        <a href={videoUrl} target="_blank" rel="noopener noreferrer" className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs hover:bg-white/40 transition-all"><Play size={14} />Watch Video</a>
      )}

      <div className="absolute bottom-6 left-6 flex gap-2">
        {allMedia.map((m, i) => (<button key={i} onClick={() => setCurrent(i)} className={`w-2 h-2 rounded-full transition-all ${current === i ? 'bg-white w-6' : 'bg-white/40'}`} />))}
      </div>
      <span className="absolute bottom-6 right-6 text-white/60 text-xs">{current + 1} / {allMedia.length}</span>
      </div>
    </div>
  );
};

export default function PropertyDetail() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState(REQUEST_TYPES.BROCHURE);
  const [showNav, setShowNav] = useState(false);
  const [phone, setPhone] = useState('919187108123');

  useEffect(() => {
    getConfig().then((c) => c.BUSINESS_PHONE && setPhone(c.BUSINESS_PHONE));
  }, []);

  useEffect(() => {
    const handleScroll = () => setShowNav(window.scrollY > 120);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    findProperty(id).then((p) => { setProperty(p || null); setLoading(false); }).catch(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center"><Loader2 className="animate-spin text-[#0B3D2E]" size={40} /></div>;
  if (!property) return <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center text-gray-500 font-body">Property not found</div>;

  const specs = [
    { icon: Layers, label: 'Land Area', value: property.land_area },
    { icon: Building, label: 'Type', value: property.property_type },
    { icon: SquareIcon, label: 'Units', value: property.units },
    { icon: Maximize, label: 'Super Built-Up Area', value: property.area },
    { icon: Trees, label: 'Carpet Area', value: property.carpet_area },
    { icon: Maximize, label: 'Balcony Area', value: property.balcony_area },
  ].filter((s) => s.value);

  const hasBrochure = !!property.brochure_url;
  const hasPricing = !!property.pricing_pdf_url;

  return (
    <div className="min-h-screen bg-[#FDFCF8] font-body">
      {/* Top Bar - appears on scroll */}
      <AnimatePresence>
        {showNav && (
          <motion.div
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            exit={{ y: -100 }}
            transition={{ duration: 0.3 }}
            className="bg-[#0B3D2E] py-3 px-6 md:px-12 fixed top-0 left-0 right-0 z-40"
            data-testid="property-top-bar"
          >
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <a href="/"><img src="/logo.png" alt="The NRI Realtor" className="h-9" /></a>
              <a href={`https://wa.me/${phone}?text=${encodeURIComponent(`Hi, I'm interested in ${property.title}.`)}&lang=en`} target="_blank" rel="noopener noreferrer" className="text-white text-sm hover:text-[#E8D48B] transition-colors flex items-center gap-2"><Phone size={15} />Contact</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <ImageCarousel images={property.images} videoUrl={property.video_url} />

      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl p-6 md:p-8 shadow-lg border border-gray-100">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                {property.builder && <span className="px-3 py-1 bg-[#0B3D2E] text-white text-xs uppercase tracking-wider rounded-full">{property.builder}</span>}
                <span className={`px-3 py-1 text-xs rounded-full ${property.status === 'Available' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>{property.status}</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-light text-gray-900 mb-2" data-testid="property-page-title">{property.title}</h1>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-4"><MapPin size={16} className="text-[#0B3D2E]" />{property.location}</div>
              <div className="flex flex-wrap items-center gap-4">
                <span className="text-3xl font-heading text-[#0B3D2E]">{property.price || 'Price on request'}</span>
                {property.pricing_range && property.price !== property.pricing_range && <span className="text-sm text-gray-500 border-l border-gray-200 pl-4">Range: <span className="text-[#C5982A] font-medium">{property.pricing_range}</span></span>}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-5 text-center border border-gray-100 shadow-sm"><Bed className="mx-auto text-[#0B3D2E] mb-2" size={22} /><p className="text-lg font-heading text-gray-900">{property.bedrooms || 'On request'}</p><p className="text-xs text-gray-400">{property.bedrooms ? 'Configuration' : 'Configuration available on request'}</p></div>
              <div className="bg-white rounded-xl p-5 text-center border border-gray-100 shadow-sm"><Maximize className="mx-auto text-[#0B3D2E] mb-2" size={22} /><p className="text-lg font-heading text-gray-900">{property.area || '—'}</p><p className="text-xs text-gray-400">Size</p></div>
              <div className="bg-white rounded-xl p-5 text-center border border-gray-100 shadow-sm"><Building className="mx-auto text-[#0B3D2E] mb-2" size={22} /><p className="text-lg font-heading text-gray-900">{property.property_type || '—'}</p><p className="text-xs text-gray-400">Type</p></div>
            </motion.div>

            {property.description && (
              <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-xl p-6 md:p-8 border border-gray-100 shadow-sm">
                <h2 className="text-2xl font-heading text-gray-900 mb-4">Project Overview</h2>
                <p className="text-gray-600 font-light leading-relaxed whitespace-pre-line">{property.description}</p>
              </motion.div>
            )}

            {property.amenities && (
              <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="bg-white rounded-xl p-6 md:p-8 border border-gray-100 shadow-sm">
                <h2 className="text-2xl font-heading text-gray-900 mb-4">Amenities</h2>
                <div className="flex flex-wrap gap-2">
                  {property.amenities.split(/[,\n]/).map((a) => a.trim()).filter(Boolean).map((a, i) => (
                    <span key={i} className="px-3 py-1.5 rounded-full bg-[#0B3D2E]/10 text-[#0B3D2E] text-sm">{a}</span>
                  ))}
                </div>
              </motion.div>
            )}

            {specs.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white rounded-xl p-6 md:p-8 border border-gray-100 shadow-sm">
                <h2 className="text-2xl font-heading text-gray-900 mb-6">Property Details</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  {specs.map((s, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[#0B3D2E]/10 flex items-center justify-center flex-shrink-0"><s.icon className="text-[#0B3D2E]" size={16} /></div>
                      <div><p className="text-xs text-gray-400">{s.label}</p><p className="text-sm text-gray-900 font-medium">{s.value}</p></div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>

          <div className="space-y-4 lg:pt-0">
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-xl p-6 border border-gray-100 shadow-lg sticky top-20">
              <h3 className="text-lg font-heading text-gray-900 mb-5">Interested?</h3>
              <div className="space-y-3">
                <button
                  onClick={() => { setFormType(REQUEST_TYPES.PRICING); setShowForm(true); }}
                  className="w-full bg-[#0B3D2E] text-white py-3 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2 text-sm"
                  data-testid="pricing-btn"
                >
                  <IndianRupee size={16} />
                  {hasPricing ? 'Get Pricing Details' : 'Pricing — Available on Request'}
                </button>
                <button
                  onClick={() => { setFormType(REQUEST_TYPES.BROCHURE); setShowForm(true); }}
                  className="w-full bg-[#0B3D2E] text-white py-3 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2 text-sm"
                  data-testid="download-brochure-btn"
                >
                  <Download size={16} />
                  {hasBrochure ? 'Download Brochure' : 'Brochure — Available on Request'}
                </button>
                <button
                  onClick={() => { setFormType(REQUEST_TYPES.GENERAL); setShowForm(true); }}
                  className="w-full bg-white border border-[#0B3D2E] text-[#0B3D2E] py-3 rounded-lg font-medium hover:bg-[#0B3D2E] hover:text-white transition-colors flex items-center justify-center gap-2 text-sm"
                  data-testid="general-enquiry-btn"
                >
                  <Send size={16} />
                  Send Enquiry
                </button>
                <a href={`https://wa.me/${phone}?text=${encodeURIComponent(`Hi, I'm interested in ${property.title} at ${property.location}. Please share details.`)}&lang=en`} target="_blank" rel="noopener noreferrer" className="w-full bg-[#25D366] text-white py-3 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2 text-sm">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  WhatsApp Us
                </a>
                <a href={`tel:+${phone}`} className="w-full border border-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:border-[#0B3D2E] hover:text-[#0B3D2E] transition-colors flex items-center justify-center gap-2 text-sm"><Phone size={16} />Call Now</a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {property.connectivity && (
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="h-[350px] lg:h-auto">
                <iframe
                  title="Map"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(property.location)}&output=embed`}
                  className="w-full h-full border-0"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <div className="p-6 md:p-8">
                <h2 className="text-2xl font-heading text-gray-900 mb-6">Know Your Neighbourhood</h2>
                <p className="text-xs uppercase tracking-widest text-[#C5982A] mb-4 font-medium">Connectivity</p>
                <div className="space-y-0">
                  {property.connectivity.split('\n').filter(l => l.trim()).map((line, i) => {
                    const parts = line.split('-').map(s => s.trim());
                    const place = parts[0] || line;
                    const distance = parts[1] || '';
                    return (
                      <div key={i} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                        <span className="text-gray-700 text-sm">{place}</span>
                        {distance && <span className="text-[#C5982A] text-sm font-medium italic">{distance}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      <LeadForm
        open={showForm}
        onClose={() => setShowForm(false)}
        requestType={formType}
        interestedIn={property.title}
        brochureLink={property.brochure_url}
        pricingLink={property.pricing_pdf_url}
        pageSource={`Property Detail — ${property.title}`}
      />
    </div>
  );
}
